import { Combobox, ComboboxProps, Spinner, Text } from "@fluentui/react-components";
import { useStaticVirtualizerMeasure } from "@fluentui/react-components/unstable";
import { debounce } from "lodash";
import { memo, ReactElement, useCallback, useRef, useState } from "react";
import { useTagData } from "../../hooks/useTagData";
import { Tag } from "../../models/tag";
import { SelectedTagsList } from "./components/SelectedTagsList";
import { TagsOptionsList } from "./components/TagsOptionsList";
import { useMetaDataSelectorStyles } from "./styles";

export type MetaDataSelectorProps = {
  label?: string | ReactElement;
  selected?: string[];
  group_id?: string;
  tags?: Tag[];
  isLoading?: boolean;
  showCategories?: boolean;
  onSelectedTagsChange?: (selectedTags: string[]) => void;
  appId: string;
};

export const MetaDataSelector = memo(
  ({ selected = [], tags = [], isLoading = false, showCategories = true, appId, ...props }: MetaDataSelectorProps) => {
    const styles = useMetaDataSelectorStyles();

    const itemHeight = 32;

    const { workspaceVisibleTags: selectedTags } = useTagData(selected, {
      workspaceId: appId,
      includeAll: false,
    });

    const [query, setQuery] = useState<string>("");

    const firstTagRef = useRef<HTMLButtonElement>(null);
    const comboboxInputRef = useRef<HTMLInputElement>(null);
    const { scrollRef } = useStaticVirtualizerMeasure({
      defaultItemSize: itemHeight,
      direction: "vertical",
    });

    const removeItem = useCallback(
      (value: string) => {
        const newSelectedTags = selected.filter((tag) => tag !== value);
        props.onSelectedTagsChange?.(newSelectedTags);
      },
      [selectedTags, props.onSelectedTagsChange]
    );

    const onSelect: ComboboxProps["onOptionSelect"] = async (_event, data) => {
      props.onSelectedTagsChange?.(data.selectedOptions);
    };

    const onInputChange: ComboboxProps["onChange"] = debounce((event) => {
      setQuery(event.target.value.trim());
    }, 300);

    const mountNode = document.getElementById("suggestion-filter-form");

    return (
      <div className={styles.container}>
        {props.label ? (
          <Text>{props.label}</Text>
        ) : (
          <Text className={styles.textFallback}>Apply metadata and tags</Text>
        )}

        <div className={styles.wrapper}>
          {selectedTags.length !== 0 && (
            <SelectedTagsList selectedTags={selectedTags} removeItem={removeItem} firstTagRef={firstTagRef} />
          )}

          <Combobox
            className={styles.selectorCombo}
            multiselect={true}
            placeholder="Insert keywords and tags"
            selectedOptions={selected}
            freeform={true}
            listbox={{ ref: scrollRef, className: styles.listbox }}
            mountNode={mountNode}
            onChange={onInputChange}
            onOptionSelect={onSelect}
            ref={comboboxInputRef}
            disabled={isLoading}
            expandIcon={isLoading ? <Spinner size="extra-tiny" /> : undefined}
          >
            <TagsOptionsList
              showCategories={showCategories}
              query={query}
              tags={tags}
              itemHeight={itemHeight}
              appId={appId}
            />
          </Combobox>
        </div>
      </div>
    );
  }
);
